import {
	useGetOfferBatchLocationQuery,
	useGetQuoteProductCommentTargetQuery,
	useGetSampleLocationQuery,
} from '@codezo/graphql';
import { threadKey } from '../../components/quotes/threads/useQuoteThreads';
import { LinkIcon } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { EmptyState } from '../../components/ui/empty-state';

/**
 * Resolver for `/n/:type/:id`, the admin half.
 *
 * Notifications carry a `(type, id)` pair and nothing about routing, so the bell
 * links here and this decides where the pair currently lives. The client app
 * owns the same path for emailed links; admins only ever reach it from the bell,
 * which is why there is no signed-out handling here.
 *
 * Adding a backend notification link type means adding a case in both.
 */

const FALLBACK_PATH = '/dashboard';

export function NotificationLinkPage() {
	const { type, id } = useParams<{ type: string; id: string }>();
	const navigate = useNavigate();

	// Admins review offers on the quote screen, so a batch id has to be traded
	// for its quote id first. Only the matching type's query runs.
	//
	// Shares the client app's query rather than the admin-only
	// `getOrderQuoteOfferBatch`: this returns the two ids the resolver needs
	// instead of the whole batch with its offers, margins and delivery plans,
	// and one query across both apps means one place for this to be wrong.
	const batch = useGetOfferBatchLocationQuery({
		variables: { id: id ?? '' },
		skip: type !== 'offer-batch' || !id,
		fetchPolicy: 'no-cache',
	});

	// Samples live on an order, and nothing else in the API gets from a sample
	// id to one. This is the admin bell's most common link by far — the internal
	// sample notifications all carry one.
	const sample = useGetSampleLocationQuery({
		variables: { id: id ?? '' },
		skip: type !== 'sample' || !id,
		fetchPolicy: 'no-cache',
	});

	const resolving = (
		<div className="flex min-h-96 items-center justify-center">
			<p className="text-sm text-text-3">Opening…</p>
		</div>
	);

	const unavailable = (
		<div className="mx-auto max-w-md px-4 py-24">
			<EmptyState
				icon={LinkIcon}
				title="This is no longer available"
				description="It may have been deleted since this notification was sent."
			>
				<Button className="mt-2" onClick={() => navigate(FALLBACK_PATH, { replace: true })}>
					Go to dashboard
				</Button>
			</EmptyState>
		</div>
	);

	// A comment names only itself; where it lives comes from the backend. Null
	// means it can no longer be opened.
	const comment = useGetQuoteProductCommentTargetQuery({
		variables: { id: id ?? '' },
		skip: type !== 'QUOTE_PRODUCT_COMMENT' || !id,
		fetchPolicy: 'no-cache',
	});

	if (!id) return <Navigate to={FALLBACK_PATH} replace />;

	switch (type) {
		case 'order':
			return <Navigate to={`/orders/${id}`} replace />;

		case 'quote':
			// Carries OFFER_LATE_ACCEPTANCE — a client accepted after the deadline.
			// Unusually for an acceptance there is no order yet: it's held pending
			// until an admin confirms, rejects or revises, so the destination is a
			// quote awaiting a decision. Internal, so admins are who arrive here.
			return <Navigate to={`/quotes/${id}`} replace />;

		case 'sample': {
			if (sample.loading) return resolving;
			const orderId = sample.data?.getSampleLocation?.orderId;
			if (sample.error || !orderId) return unavailable;
			// The order page is where a sample is actioned. No anchor for an
			// individual sample yet — `orderItemId` is what that would need, and
			// the query already returns it.
			return <Navigate to={`/orders/${orderId}`} replace />;
		}

		case 'offer-batch': {
			if (batch.loading) return resolving;
			const quoteId = batch.data?.getOfferBatchLocation?.orderQuoteId;
			if (batch.error || !quoteId) return unavailable;
			return <Navigate to={`/quotes/${quoteId}`} replace />;
		}

		case 'QUOTE_PRODUCT_COMMENT': {
			if (comment.loading) return resolving;
			const target = comment.data?.getQuoteProductCommentTarget;
			if (comment.error || !target) return unavailable;
			// Open the quote with that product's threads, on that thread's tab.
			const params = new URLSearchParams({
				comments: target.orderQuoteItemId,
				thread: threadKey(target),
			});
			return <Navigate to={`/quotes/${target.orderQuoteId}?${params}`} replace />;
		}

		default:
			// Backends deploy ahead of frontends — somewhere useful beats an error.
			return <Navigate to={FALLBACK_PATH} replace />;
	}
}
